import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, 
  TextField, Select, MenuItem, FormControl, InputLabel, Grid, Paper, Container,
  Card, CardContent, Chip, IconButton, Tooltip, Autocomplete
} from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import AddIcon from '@mui/icons-material/Add';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import axios from 'axios';
import Loading from '../components/Loading';
import API_URL from '../config';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Haversine formula to calculate distance between two points in km
function calculateDistance(lat1, lon1, lat2, lon2) {
  if (!lat1 || !lon1 || !lat2 || !lon2) return null;
  
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * 
    Math.sin(dLon/2) * Math.sin(dLon/2)
    ; 
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
  const d = R * c; // Distance in km
  return d;
}

function deg2rad(deg) {
  return deg * (Math.PI/180);
}

const WORK_TYPES = [
  { category: 'Welding NDT Inspection Services', title: 'QA/QC Mechanical Inspector' },
  { category: 'Welding NDT Inspection Services', title: 'Welding Inspector (CSWIP 3.1)' },
  { category: 'Welding NDT Inspection Services', title: 'NDT Level III Services' },
  { category: 'Welding NDT Inspection Services', title: 'Visual Inspection VT' },
  { category: 'Welding NDT Inspection Services', title: 'Radiographic Testing (XRAY Technic)' },
  { category: 'Welding NDT Inspection Services', title: 'RTFI Inspector' },
  { category: 'Welding NDT Inspection Services', title: 'Dye Penetrant Testing (DPT)' },
  { category: 'Welding NDT Inspection Services', title: 'Magnetic Particle Testing (MPT)' },
  { category: 'Welding NDT Inspection Services', title: 'Ultrasonic Testing (UT)' },
  { category: 'Welding NDT Inspection Services', title: 'Fit up Inspection' },
  { category: 'Welding NDT Inspection Services', title: 'Post Weld Heat Treatment (PWHT)' },

  { category: 'Steel NDT Services', title: 'Steel Structure Condition Assesment' },
  { category: 'Steel NDT Services', title: 'Ultrasonic Thickness Test (UTT)' },
  { category: 'Steel NDT Services', title: 'Dimentional Test (DT)' },
  { category: 'Steel NDT Services', title: 'Manual Bolt Tightening Test (Manual Torque Wrenches)' },
  { category: 'Steel NDT Services', title: 'Digital Bolt Tightening Test (Digital Torque Measurment)' },
  { category: 'Steel NDT Services', title: 'Hardness Test (HT)' },
  { category: 'Steel NDT Services', title: 'Ferrite Test (FT)' },
  { category: 'Steel NDT Services', title: 'Positive Material Identification (PMI)' },
  { category: 'Steel NDT Services', title: 'Shear Stud Test' },
  { category: 'Steel NDT Services', title: 'Macro Etching Test' },

  { category: 'Painting NDT Inspection Services', title: 'Surface Preparation Services' },
  { category: 'Painting NDT Inspection Services', title: 'Roughness Test' },
  { category: 'Painting NDT Inspection Services', title: 'Dust Contamination Test' },
  { category: 'Painting NDT Inspection Services', title: 'Wet Film Thickness (WFT)' },
  { category: 'Painting NDT Inspection Services', title: 'Dry Film Thickness Test (DFT)' },
  { category: 'Painting NDT Inspection Services', title: 'Coating Holiday Test' },
  { category: 'Painting NDT Inspection Services', title: 'Painting Inspector (BGAS)' },

  { category: 'Welder Certification & Qualification', title: 'Preparing / Revising WPS' },
  { category: 'Welder Certification & Qualification', title: 'NDT & WPQR Testing & Records' },

  { category: 'NDT Special Testing', title: 'Water Leakage Test (as per AAMA 501.2)' },
  { category: 'NDT Special Testing', title: 'Water Leakage Test (as per ASTM E1105)' },
  { category: 'NDT Special Testing', title: 'Air Infiltration Test (as per ASTM E783)' },
  { category: 'NDT Special Testing', title: 'Glass Sealant Adhesion Test' },
  { category: 'NDT Special Testing', title: 'Fire Stop Inspection Services' },
  { category: 'NDT Special Testing', title: 'Fire Stop Surveil Services (before/during/after application)' },
  { category: 'NDT Special Testing', title: 'Geomembrane Inspection Services' },
  { category: 'NDT Special Testing', title: 'Dry Roof Test' },
  { category: 'NDT Special Testing', title: 'Vacuum Box Test' },
  { category: 'NDT Special Testing', title: 'Spark Test' },
  { category: 'NDT Special Testing', title: 'NDT Borescope' },
];


function LocationMap({ location }) {
  if (!location) return <Typography variant="caption" color="text.secondary">No location data</Typography>;
  
  const parts = location.split(',');
  if (parts.length !== 2) return <Typography variant="caption" color="text.secondary">Invalid location format</Typography>;
  
  const lat = parseFloat(parts[0]);
  const lng = parseFloat(parts[1]);
  
  if (isNaN(lat) || isNaN(lng)) return <Typography variant="caption" color="text.secondary">Invalid coordinates</Typography>;

  return (
    <Box sx={{ height: 200, width: '100%', mt: 1, mb: 2, borderRadius: 1, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
       <MapContainer center={[lat, lng]} zoom={15} style={{ height: '100%', width: '100%' }}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          <Marker position={[lat, lng]} />
       </MapContainer>
    </Box>
  );
}

function Datasheets() {
  const navigate = useNavigate();
  const location = useLocation();
  const [events, setEvents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedUserFilter, setSelectedUserFilter] = useState('all');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('all');
  
  // Edit Dialog State
  const [editOpen, setEditOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editFormData, setEditFormData] = useState({
    id: '',
    project_id: '',
    assigned_to_id: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    type_of_work: '',
    location: '',
    description: '',
    checkIn: '',
    checkOut: '',
    checkInLocation: '',
    checkOutLocation: ''
  });
  
  // Form State
  const [formData, setFormData] = useState({
    project_id: '',
    assigned_to_id: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    type_of_work: '',
    location: '',
    description: ''
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  // In a real app, use Context for user info. Assuming user_id=1 for now or decoded from token in a real scenario.
  // For this demo, we'll let the backend handle the user_id from the token if possible, 
  // but the current API expects user_id in the body. I'll decode it or just use a placeholder if I can't.
  // The login response returns user_id, let's assume it's stored or we can get it.
  const userId = localStorage.getItem('user_id') || 1; 
  const role = localStorage.getItem('user_role') || ''; 

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch Schedule
      try {
        const eventsRes = await axios.get(`${API_URL}/schedule`, { headers });
        const formattedEvents = eventsRes.data.map(e => {
          const currentUserId = parseInt(userId);
          let bgColor = '#3B82F6'; // Default Blue
          
          if (e.assigned_to_id) {
             if (e.assigned_to_id !== currentUserId) {
                 bgColor = '#ED6C02'; // Orange (Assigned to others)
             } else if (e.user_id !== currentUserId) {
                 bgColor = '#9C27B0'; // Purple (Assigned to me by others)
             }
          }

          return {
            id: e.id,
            title: `${e.project_name} - ${e.type_of_work || 'Work'}`,
            start: e.start,
            end: e.end,
            extendedProps: {
              description: e.description,
              type_of_work: e.type_of_work,
              location: e.location,
              project: e.project_name,
              project_id: e.project_id,
              project_latitude: e.project_latitude,
              project_longitude: e.project_longitude,
              assigned_to_id: e.assigned_to_id,
              assigned_to: e.assigned_to,
              user_id: e.user_id,
              user: e.user,
              check_in: e.check_in,
              check_out: e.check_out,
              check_in_location: e.check_in_location,
              check_out_location: e.check_out_location
            },
            backgroundColor: bgColor,
            borderColor: bgColor
          };
        });
        setEvents(formattedEvents);
      } catch (error) {
        console.error("Error fetching schedule:", error);
      }

      // Fetch Projects
      try {
        const projectsRes = await axios.get(`${API_URL}/projects`, { headers });
        console.log("Projects fetched:", projectsRes.data);
        setProjects(projectsRes.data);
      } catch (error) {
        console.error("Error fetching projects:", error);
      }

      // Fetch Users
      try {
        const usersRes = await axios.get(`${API_URL}/setup/users`, { headers });
        setUsers(usersRes.data);
      } catch (error) {
        console.error("Error fetching users:", error);
      }

    } catch (err) {
      console.error(err);
      if (err.response && err.response.status === 401) {
        alert("Session expired. Please login again.");
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getLocation = (callback) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        const loc = `${position.coords.latitude}, ${position.coords.longitude}`;
        callback(loc);
      }, (error) => {
        console.error("Error getting location:", error);
        callback("Location unavailable");
      });
    } else {
      callback("Geolocation not supported");
    }
  };

  const filteredEvents = events.filter(event => {
      // User Filter
      let userMatch = true;
      if (selectedUserFilter !== 'all') {
          const filterId = parseInt(selectedUserFilter);
          userMatch = event.extendedProps.user_id === filterId || event.extendedProps.assigned_to_id === filterId;
      }

      // Status Filter
      let statusMatch = true;
      if (selectedStatusFilter !== 'all') {
          const { check_in, check_out } = event.extendedProps;
          if (selectedStatusFilter === 'no_check') statusMatch = !check_in;
          else if (selectedStatusFilter === 'check_in') statusMatch = check_in && !check_out;
          else if (selectedStatusFilter === 'check_out') statusMatch = check_out;
      }

      return userMatch && statusMatch;
  });

  const handleCreate = async () => {
    // Validation
    if (!formData.startDate || !formData.startTime) {
      alert("Please fill in start date and time.");
      return;
    }
    if (!formData.type_of_work) {
        alert("Please specify the type of work.");
        return;
    }

    // Combine Date and Time
    const start = `${formData.startDate}T${formData.startTime}`;
    const end = formData.endDate && formData.endTime ? `${formData.endDate}T${formData.endTime}` : null;

    setCreateLoading(true);
    try {
      await axios.post(`${API_URL}/schedule`, {
        // user_id is handled by backend via token
        project_id: formData.project_id,
        assigned_to_id: formData.assigned_to_id,
        title: formData.type_of_work, // Use type of work as title
        start,
        end,
        type_of_work: formData.type_of_work,
        location: formData.location,
        description: formData.description
      }, { headers });
      
      setOpen(false);
      // Reset form
      setFormData({
        project_id: '',
        assigned_to_id: '',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        type_of_work: '',
        location: '',
        description: ''
      });
      fetchData();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || "Please check all fields.";
      alert(`Error creating entry: ${msg}`);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API_URL}/schedule/export`, {
        headers,
        responseType: 'blob', // Important for file download
      });
      
      // Create a link to download the file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'timesheet_report.pdf');
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
      alert("Failed to export report. Please try again.");
    }
  };

  const handleEventClick = (info) => {
    const event = info.event;
    const start = new Date(event.start);
    const end = new Date(event.end);
    
    // Helper to format date as YYYY-MM-DD
    const formatDate = (d) => d.toISOString().split('T')[0];
    // Helper to format time as HH:MM
    const formatTime = (d) => d.toTimeString().split(' ')[0].substring(0, 5);

    // Helper for datetime-local
    const formatDateTime = (d) => {
        if (!d) return '';
        const pad = (num) => (num < 10 ? '0' : '') + num;
        return (
            d.getFullYear() +
            '-' +
            pad(d.getMonth() + 1) +
            '-' +
            pad(d.getDate()) +
            'T' +
            pad(d.getHours()) +
            ':' +
            pad(d.getMinutes())
        );
    };

    const checkIn = event.extendedProps.check_in ? new Date(event.extendedProps.check_in) : null;
    const checkOut = event.extendedProps.check_out ? new Date(event.extendedProps.check_out) : null;

    setEditFormData({
        id: event.id,
        project_id: event.extendedProps.project_id || '',
        assigned_to_id: event.extendedProps.assigned_to_id || '',
        startDate: formatDate(start),
        startTime: formatTime(start),
        endDate: end ? formatDate(end) : '',
        endTime: end ? formatTime(end) : '',
        type_of_work: event.extendedProps.type_of_work,
        location: event.extendedProps.location || '',
        description: event.extendedProps.description,
        checkIn: checkIn ? formatDateTime(checkIn) : '',
        checkOut: checkOut ? formatDateTime(checkOut) : '',
        checkInLocation: event.extendedProps.check_in_location || '',
        checkOutLocation: event.extendedProps.check_out_location || ''
    });
    setEditOpen(true);
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const editId = searchParams.get('editId');
    const openEventId = location.state?.openEventId;
    
    const targetId = editId ? parseInt(editId) : (openEventId ? parseInt(openEventId) : null);

    if (targetId && events.length > 0) {
        const event = events.find(e => e.id === targetId);
        if (event) {
             // Only open if not already open to avoid loops/flickering
             if (!editOpen) {
                handleEventClick({ event: event });
                // Clear the trigger so it doesn't persist
                if (editId) {
                    navigate(location.pathname, { replace: true });
                } else if (openEventId) {
                    // Clear state
                    navigate(location.pathname, { replace: true, state: {} });
                }
             }
        }
    }
  }, [events, location.search, location.state, editOpen, navigate, location.pathname]);

  const handleUpdateEvent = async () => {
    if (!editFormData.startDate || !editFormData.startTime) {
        alert("Please fill in start date and time.");
        return;
    }

    const start = `${editFormData.startDate}T${editFormData.startTime}`;
    const end = editFormData.endDate && editFormData.endTime ? `${editFormData.endDate}T${editFormData.endTime}` : null;
    
    const check_in = editFormData.checkIn || null;
    const check_out = editFormData.checkOut || null;

    try {
        await axios.put(`${API_URL}/schedule/${editFormData.id}`, {
            project_id: editFormData.project_id,
            assigned_to_id: editFormData.assigned_to_id,
            start,
            end,
            type_of_work: editFormData.type_of_work,
            location: editFormData.location,
            description: editFormData.description,
            check_in,
            check_out,
            check_in_location: editFormData.checkInLocation,
            check_out_location: editFormData.checkOutLocation
        }, { headers });
        
        setEditOpen(false);
        fetchData();
    } catch (err) {
        console.error(err);
        alert("Failed to update event");
    }
  };

  const handleDeleteEvent = async () => {
      if (!window.confirm("Are you sure you want to delete this entry?")) return;
      
      try {
          await axios.delete(`${API_URL}/schedule/${editFormData.id}`, { headers });
          setEditOpen(false);
          fetchData();
      } catch (err) {
          console.error(err);
          alert("Failed to delete event");
      }
  };

  const renderEventContent = (eventInfo) => {
    const { event } = eventInfo;
    const isCheckedIn = event.extendedProps.check_in;
    const isCheckedOut = event.extendedProps.check_out;
    const assignedTo = event.extendedProps.assigned_to;
    const createdBy = event.extendedProps.user;
    const currentUserId = parseInt(userId);
    const assignedToId = event.extendedProps.assigned_to_id;
    const creatorId = event.extendedProps.user_id;
    const checkInLocation = event.extendedProps.check_in_location;
    const projectLat = event.extendedProps.project_latitude;
    const projectLng = event.extendedProps.project_longitude;

    // Determine Status Color and Background
    let statusColor = event.backgroundColor; 
    let bgColor = '#ffffff'; // Default white
    let borderColor = '#e0e0e0';

    if (isCheckedOut) {
        statusColor = '#2e7d32'; // Darker Green
        bgColor = '#f1f8e9'; // Very Light Green
        borderColor = '#c5e1a5';
    } else if (isCheckedIn) {
        statusColor = '#1565c0'; // Darker Blue
        bgColor = '#e3f2fd'; // Very Light Blue
        borderColor = '#90caf9';
    } else {
        statusColor = '#ef6c00'; // Darker Orange
        bgColor = '#fff3e0'; // Very Light Orange
        borderColor = '#ffcc80';
    }

    let locationStatus = null;
    if (checkInLocation && projectLat && projectLng) {
        const parts = checkInLocation.split(',');
        if (parts.length === 2) {
            const checkInLat = parseFloat(parts[0]);
            const checkInLng = parseFloat(parts[1]);
            const distance = calculateDistance(checkInLat, checkInLng, projectLat, projectLng);
            
            if (distance !== null) {
                const isNear = distance < 0.5; // 500 meters
                locationStatus = (
                    <Tooltip title={isNear ? "On Site" : `Off Site (${distance.toFixed(2)} km)`}>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            bgcolor: isNear ? 'rgba(46, 125, 50, 0.1)' : 'rgba(211, 47, 47, 0.1)',
                            color: isNear ? 'success.dark' : 'error.dark',
                            px: 0.5, 
                            borderRadius: 10,
                            border: `1px solid ${isNear ? 'rgba(46, 125, 50, 0.3)' : 'rgba(211, 47, 47, 0.3)'}`,
                            fontSize: '0.6rem',
                            fontWeight: 'bold',
                            ml: 'auto',
                            height: 18
                        }}>
                            <LocationOnIcon sx={{ fontSize: 10, mr: 0.2 }} />
                            {isNear ? 'On Site' : `${distance.toFixed(1)}km`}
                        </Box>
                    </Tooltip>
                );
            }
        }
    }
    
    return (
      <Box sx={{ 
        p: 0.8, 
        width: '100%', 
        overflow: 'hidden',
        bgcolor: bgColor,
        border: `1px solid ${borderColor}`,
        borderLeft: `3px solid ${statusColor}`,
        color: 'text.primary',
        borderRadius: 1.5,
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        mb: 0.5,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: 3, zIndex: 10 }
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem', lineHeight: 1.2 }} noWrap>
                {event.extendedProps.project || 'No Project'}
            </Typography>
        </Box>
        
        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block', mb: 0.5 }} noWrap>
            {event.extendedProps.type_of_work || 'Work Log'}
        </Typography>

        <Box sx={{ mb: 0.5 }}>
             <Typography variant="caption" display="block" sx={{ fontSize: '0.65rem', color: 'text.secondary' }} noWrap>
                <Box component="span" sx={{ fontWeight: 'bold' }}>By:</Box> {createdBy}
             </Typography>
             {assignedTo && (
                 <Typography variant="caption" display="block" sx={{ fontSize: '0.65rem', color: 'primary.main' }} noWrap>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>To:</Box> {assignedTo}
                 </Typography>
             )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', fontWeight: 'medium' }}>
                {eventInfo.timeText}
            </Typography>
            {locationStatus}
        </Box>
      </Box>
    );
  };

  if (loading) return <Loading message="Loading TimeSheet..." />;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header Section */}
      <Paper elevation={0} sx={{  
        p: 4, 
        mb: 4, 
        borderRadius: 4, 
        background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)', 
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Decorative Circles */}
        <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)' }} />
        <Box sx={{ position: 'absolute', bottom: -30, left: 100, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)' }} />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'flex-start' }, flexWrap: 'wrap', gap: 3 }}>
                {/* Title & Status Filters */}
                <Box>
                    <Typography variant="h3" fontWeight="800" sx={{ mb: 3, letterSpacing: -1, fontSize: { xs: '2rem', md: '3rem' } }}>
                        TimeSheet
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {[
                            { label: 'All', value: 'all' },
                            { label: 'Pending', value: 'no_check' },
                            { label: 'In Progress', value: 'check_in' },
                            { label: 'Completed', value: 'check_out' }
                        ].map(f => (
                            <Chip 
                                key={f.value} 
                                label={f.label} 
                                onClick={() => setSelectedStatusFilter(f.value)} 
                                sx={{ 
                                    fontWeight: 'bold',
                                    bgcolor: selectedStatusFilter === f.value ? 'white' : 'rgba(255,255,255,0.1)',
                                    color: selectedStatusFilter === f.value ? 'primary.main' : 'white',
                                    '&:hover': { bgcolor: selectedStatusFilter === f.value ? 'white' : 'rgba(255,255,255,0.2)' }
                                }}
                            />
                        ))}
                    </Box>
                </Box>

                {/* Actions & Filters */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
                        <Select
                            value={selectedUserFilter}
                            onChange={(e) => setSelectedUserFilter(e.target.value)}
                            displayEmpty
                            sx={{ 
                                bgcolor: 'rgba(255,255,255,0.1)', 
                                color: 'white',
                                borderRadius: 2,
                                '.MuiSelect-icon': { color: 'rgba(255,255,255,0.7)' },
                                '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                '&:hover fieldset': { borderColor: 'white' },
                                '&.Mui-focused fieldset': { borderColor: 'white' }
                            }}
                            renderValue={(selected) => {
                                if (selected === 'all') return <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><PersonIcon fontSize="small" /> All Users</Box>;
                                const user = users.find(u => u.id === selected);
                                return <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><PersonIcon fontSize="small" /> {user ? user.full_name : 'Unknown'}</Box>;
                            }}
                        >
                            <MenuItem value="all">All Users</MenuItem>
                            {users.map(u => (
                                <MenuItem key={u.id} value={u.id}>{u.full_name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
                        <Button 
                            variant="contained" 
                            onClick={handleExport} 
                            startIcon={<PictureAsPdfIcon />}
                            fullWidth
                            sx={{ 
                                bgcolor: 'rgba(255,255,255,0.2)', 
                                backdropFilter: 'blur(10px)',
                                color: 'white', 
                                boxShadow: 'none',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                                flex: { xs: 1, sm: 'initial' }
                            }}
                        >
                            Export PDF
                        </Button>
                        <Button 
                            variant="contained" 
                            onClick={() => setOpen(true)} 
                            startIcon={<AddIcon />}
                            fullWidth
                            sx={{ 
                                bgcolor: 'white', 
                                color: 'primary.main',
                                fontWeight: 'bold',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                                flex: { xs: 1, sm: 'initial' }
                            }}
                        >
                            Add Entry
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Calendar Section */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, borderRadius: 3, height: '100%', bgcolor: '#f8f9fa' }} elevation={0} variant="outlined">
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
              }}
              initialView="dayGridMonth"
              events={filteredEvents}
              height="75vh"
              eventClick={handleEventClick}
              eventContent={renderEventContent}
              dayMaxEvents={true}
              eventDisplay="block"
              views={{
                  dayGridMonth: {
                      dayMaxEvents: 2
                  }
              }}
            />
          </Paper>
        </Grid>

        {/* Recent / Summary Section */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }} elevation={0} variant="outlined">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">Recent Entries</Typography>
              <IconButton size="small" onClick={fetchData}><RefreshIcon /></IconButton>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {filteredEvents.slice(0, 5).map((event) => {
                const checkInLocation = event.extendedProps.check_in_location;
                const projectLat = event.extendedProps.project_latitude;
                const projectLng = event.extendedProps.project_longitude;
                let locationStatus = null;
                
                if (checkInLocation && projectLat && projectLng) {
                    const parts = checkInLocation.split(',');
                    if (parts.length === 2) {
                        const checkInLat = parseFloat(parts[0]);
                        const checkInLng = parseFloat(parts[1]);
                        const distance = calculateDistance(checkInLat, checkInLng, projectLat, projectLng);
                        
                        if (distance !== null) {
                            const isNear = distance < 0.5;
                            locationStatus = (
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                    <LocationOnIcon sx={{ fontSize: 16, mr: 0.5, color: isNear ? '#4caf50' : '#f44336' }} />
                                    <Typography variant="caption" color={isNear ? 'success.main' : 'error.main'}>
                                        {isNear ? 'On Site' : `Off Site (${distance.toFixed(2)} km)`}
                                    </Typography>
                                </Box>
                            );
                        }
                    }
                }

                return (
                <Card 
                  key={event.id} 
                  variant="outlined" 
                  sx={{ borderRadius: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                  onClick={() => handleEventClick({ event: event })}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Typography variant="subtitle2" color="primary" fontWeight="bold" gutterBottom>
                      {event.extendedProps.project}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {event.extendedProps.type_of_work || 'Work Log'}
                    </Typography>
                    {locationStatus}
                    {event.extendedProps.assigned_to && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
                            Assigned to: {event.extendedProps.assigned_to}
                        </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      {new Date(event.start).toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              )})}
              {filteredEvents.length === 0 && (
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  No entries found.
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Add Entry Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Add New Datasheet Entry</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={projects}
                getOptionLabel={(option) => {
                    const label = option.name || "";
                    return option.ref ? `${label} (${option.ref})` : label;
                }}
                value={projects.find(p => p.id === formData.project_id) || null}
                onChange={(event, newValue) => {
                    setFormData({ ...formData, project_id: newValue ? newValue.id : '' });
                }}
                renderInput={(params) => <TextField {...params} label="Project" fullWidth />}
              />
            </Grid>

            {(role.toLowerCase() === 'admin' || role.toLowerCase() === 'manager') && (
            <Grid item xs={12}>
              <Autocomplete
                options={users}
                getOptionLabel={(option) => {
                    const label = option.full_name || option.username || "";
                    return `${label} (${option.username})`;
                }}
                value={users.find(u => u.id === formData.assigned_to_id) || null}
                onChange={(event, newValue) => {
                    setFormData({ ...formData, assigned_to_id: newValue ? newValue.id : '' });
                }}
                renderInput={(params) => <TextField {...params} label="Assign To" placeholder="Myself" fullWidth />}
              />
            </Grid>
            )}

            <Grid item xs={6}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                  label="Start Time"
                  type="time"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={formData.startTime}
                  onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="End Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                  label="End Time"
                  type="time"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={formData.endTime}
                  onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={WORK_TYPES}
                groupBy={(option) => option.category}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.title}
                inputValue={formData.type_of_work}
                onInputChange={(event, newInputValue) => {
                    setFormData({ ...formData, type_of_work: newInputValue });
                }}
                renderInput={(params) => (
                    <TextField 
                        {...params} 
                        label="Type of Work" 
                        fullWidth 
                        placeholder="e.g. Visual Inspection VT"
                    />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Location"
                fullWidth
                placeholder="e.g. Office, Home, Client Site"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={createLoading}>
            {createLoading ? 'Saving...' : 'Save Entry'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Entry Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Edit Datasheet Entry</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="edit-project-select-label">Project</InputLabel>
                <Select
                  labelId="edit-project-select-label"
                  value={editFormData.project_id}
                  label="Project"
                  onChange={(e) => setEditFormData({ ...editFormData, project_id: e.target.value })}
                >
                  <MenuItem value="">
                      <em>No Project (General Work)</em>
                  </MenuItem>
                  {projects.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.name} ({p.ref})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {(role.toLowerCase() === 'admin' || role.toLowerCase() === 'manager') && (
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="edit-assign-select-label">Assign To</InputLabel>
                <Select
                  labelId="edit-assign-select-label"
                  value={editFormData.assigned_to_id}
                  label="Assign To"
                  onChange={(e) => setEditFormData({ ...editFormData, assigned_to_id: e.target.value })}
                >
                  <MenuItem value="">
                      <em>Myself</em>
                  </MenuItem>
                  {users.map((u) => (
                    <MenuItem key={u.id} value={u.id}>{u.full_name} ({u.username})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            )}

            <Grid item xs={6}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={editFormData.startDate}
                onChange={(e) => setEditFormData({ ...editFormData, startDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                  label="Start Time"
                  type="time"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={editFormData.startTime}
                  onChange={(e) => setEditFormData({ ...editFormData, startTime: e.target.value })}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="End Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={editFormData.endDate}
                onChange={(e) => setEditFormData({ ...editFormData, endDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                  label="End Time"
                  type="time"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={editFormData.endTime}
                  onChange={(e) => setEditFormData({ ...editFormData, endTime: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>Check In / Out</Typography>
            </Grid>

            {/* Check In Section */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                    label="Check In"
                    type="datetime-local"
                    fullWidth
                    InputLabelProps={{ shrink: true, readOnly: true }}
                    value={editFormData.checkIn}
                />
                <Button 
                    variant="contained" 
                    size="small" 
                    color="success"
                    onClick={() => {
                        const now = new Date();
                        const pad = (num) => (num < 10 ? '0' : '') + num;
                        const localISOTime = (
                            now.getFullYear() +
                            '-' +
                            pad(now.getMonth() + 1) +
                            '-' +
                            pad(now.getDate()) +
                            'T' +
                            pad(now.getHours()) +
                            ':' +
                            pad(now.getMinutes())
                        );
                        
                        getLocation((loc) => {
                            setEditFormData(prev => ({
                                ...prev,
                                checkIn: localISOTime,
                                checkInLocation: loc
                            }));
                        });
                    }}
                >
                    In
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Check In Location</Typography>
                    {(() => {
                        if (editFormData.checkInLocation && editFormData.project_id) {
                            const project = projects.find(p => p.id === editFormData.project_id);
                            if (project && project.latitude && project.longitude) {
                                const parts = editFormData.checkInLocation.split(',');
                                if (parts.length === 2) {
                                    const lat = parseFloat(parts[0]);
                                    const lng = parseFloat(parts[1]);
                                    const dist = calculateDistance(lat, lng, project.latitude, project.longitude);
                                    if (dist !== null) {
                                        const isNear = dist < 0.5;
                                        return (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <LocationOnIcon sx={{ fontSize: 16, mr: 0.5, color: isNear ? '#4caf50' : '#f44336' }} />
                                                <Typography variant="caption" color={isNear ? 'success.main' : 'error.main'} fontWeight="bold">
                                                    {isNear ? 'On Site' : `Off Site (${dist.toFixed(2)} km)`}
                                                </Typography>
                                            </Box>
                                        );
                                    }
                                }
                            }
                        }
                        return null;
                    })()}
                </Box>
                <LocationMap location={editFormData.checkInLocation} />
            </Grid>

            {/* Check Out Section */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                    label="Check Out"
                    type="datetime-local"
                    fullWidth
                    InputLabelProps={{ shrink: true, readOnly: true }}
                    value={editFormData.checkOut}
                />
                <Button 
                    variant="contained" 
                    size="small" 
                    color="error"
                    onClick={() => {
                        const now = new Date();
                        const pad = (num) => (num < 10 ? '0' : '') + num;
                        const localISOTime = (
                            now.getFullYear() +
                            '-' +
                            pad(now.getMonth() + 1) +
                            '-' +
                            pad(now.getDate()) +
                            'T' +
                            pad(now.getHours()) +
                            ':' +
                            pad(now.getMinutes())
                        );

                        getLocation((loc) => {
                            setEditFormData(prev => ({
                                ...prev,
                                checkOut: localISOTime,
                                checkOutLocation: loc
                            }));
                        });
                    }}
                >
                    Out
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Check Out Location</Typography>
                    {(() => {
                        if (editFormData.checkOutLocation && editFormData.project_id) {
                            const project = projects.find(p => p.id === editFormData.project_id);
                            if (project && project.latitude && project.longitude) {
                                const parts = editFormData.checkOutLocation.split(',');
                                if (parts.length === 2) {
                                    const lat = parseFloat(parts[0]);
                                    const lng = parseFloat(parts[1]);
                                    const dist = calculateDistance(lat, lng, project.latitude, project.longitude);
                                    if (dist !== null) {
                                        const isNear = dist < 0.5;
                                        return (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <LocationOnIcon sx={{ fontSize: 16, mr: 0.5, color: isNear ? '#4caf50' : '#f44336' }} />
                                                <Typography variant="caption" color={isNear ? 'success.main' : 'error.main'} fontWeight="bold">
                                                    {isNear ? 'On Site' : `Off Site (${dist.toFixed(2)} km)`}
                                                </Typography>
                                            </Box>
                                        );
                                    }
                                }
                            }
                        }
                        return null;
                    })()}
                </Box>
                <LocationMap location={editFormData.checkOutLocation} />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={WORK_TYPES}
                groupBy={(option) => option.category}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.title}
                inputValue={editFormData.type_of_work}
                onInputChange={(event, newInputValue) => {
                    setEditFormData({ ...editFormData, type_of_work: newInputValue });
                }}
                renderInput={(params) => (
                    <TextField 
                        {...params} 
                        label="Type of Work" 
                        fullWidth 
                    />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Location"
                fullWidth
                value={editFormData.location}
                onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button onClick={handleDeleteEvent} color="error">Delete</Button>
          <Box>
            <Button onClick={() => setEditOpen(false)} color="inherit" sx={{ mr: 1 }}>Cancel</Button>
            <Button onClick={handleUpdateEvent} variant="contained">Update</Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Datasheets;
